/**
 * ScrollTo hook
 *
 * Hook used to scroll the document or a specific container to an element
 * with an optional offset, taking into account horizontal centering.
 *
 * The hook supports the following options as data attributes:
 *
 *   * data-target - ID of the target element to scroll to when the hook is clicked.
 *   * data-offset - Additional offset to subtract from the top or left
 *                   in the case of fixed nav bars/elements at the top of the page or sidebars.
 *   * data-behavior - Scroll behavior, e.g., 'smooth', 'instant' (default is 'smooth').
 *   * data-scroll-target - ID of the container to be scrolled.
 *                         If not provided, it will default to the window.
 *   * data-horizontal-center - If set to 'true', the scrolling will
 *                              attempt to horizontally center the target.
 *
 * Example usage:
 *
 *   <button id={"prd-list-sel-#{block.id}"}
 *           phx-hook="ScrollTo"
 *           data-offset="50"
 *           data-target={block.id}
 *           data-scroll-target="product-list"
 *           data-horizontal-center="true">
 *     <%= block.name %>
 *   </button>
 */

function generateScrollFunc(
  scrollTarget,
  horizontalCenter,
  behavior = "smooth",
  offset = 0,
) {
  return function scrollFunc(targetElement) {
    if (horizontalCenter) {
      const itemWidth = targetElement.getBoundingClientRect().width;
      const containerWidth = scrollTarget.getBoundingClientRect().width;
      const pos =
        targetElement.getBoundingClientRect().left -
        scrollTarget.getBoundingClientRect().left -
        offset +
        itemWidth / 2 -
        containerWidth / 2;
      scrollTarget.scrollBy({ behavior: behavior, left: pos });
    } else {
      const pos =
        targetElement.getBoundingClientRect().top -
        scrollTarget.getBoundingClientRect().top -
        offset;
      scrollTarget.scrollBy({ behavior: behavior, top: pos });
    }
  };
}

const ScrollTo = {
  mounted() {
    const target = document.getElementById(this.el.dataset.target);

    const scrollTarget = this.el.dataset.scrollTarget
      ? document.getElementById(this.el.dataset.scrollTarget)
      : document.body;

    const behavior = this.el.dataset.behavior || "smooth";
    const offset = parseInt(this.el.dataset.offset, 10) || 0;
    const horizontalCenter = this.el.dataset.horizontalCenter === "true";
    const scrollFunc = generateScrollFunc(
      scrollTarget,
      horizontalCenter,
      behavior,
      offset,
    );

    if (target) {
      this.el.addEventListener("click", () => {
        scrollFunc(target);
      });
    }

    this.handleEvent("scroll_to", ({ selector }) => {
      const targetElement =
        scrollTarget && scrollTarget.querySelector(selector);
      if (targetElement) {
        scrollFunc(targetElement);
      }
    });

    window.addEventListener("scroll-selector", (event) => {
      const targetId = `#${event.detail.target}`;
      const scrollTargetId = `#${event.detail.scrollTarget}`;
      const horizontalCenter = event.detail.horizontalCenter === "true";

      const targetElement = document.querySelector(targetId);
      const scrollTargetElement = document.querySelector(scrollTargetId);

      if (targetElement && scrollTargetElement) {
        let eventScrollFunc = generateScrollFunc(
          scrollTargetElement,
          horizontalCenter,
        );
        eventScrollFunc(targetElement);
      }
    });
  },
};

export default ScrollTo;
